export type AttentiveEntry = {
  creative_id?: string;
  creative_type?: string;
  event?: string;
  source?: string;
};

export type DataLayerPush = (...args: Record<string, unknown>[]) => number;

type DataLayerInstallState = {
  installed: boolean;
  originalPush: DataLayerPush | null;
};

const ATTENTIVE_ENTRY_KEYS = [
  "creative_id",
  "creative_type",
  "event",
  "source",
] as const satisfies ReadonlyArray<keyof AttentiveEntry>;

const IMPRESSION_COUNT_COOKIE =
  /(?:^|;\s*)attn_fullscreen_impression_count=([^;]*)/;
const IMPRESSION_COUNT_COOKIE_NAME = "attn_fullscreen_impression_count";
/** Max wait when attn_fullscreen_impression_count is not set yet (first show). */
export const IMPRESSION_COUNT_WAIT_MS = 2_000;
/** Max wait when the cookie exists but may not have incremented yet. */
export const IMPRESSION_COUNT_UPDATE_WAIT_MS = 250;

export function readImpressionCountCookie(): string | undefined {
  return document.cookie.match(IMPRESSION_COUNT_COOKIE)?.[1];
}

function impressionCountIsReady(
  initialCount: string | undefined,
  currentCount: string | undefined
): boolean {
  return (
    currentCount !== undefined &&
    (initialCount === undefined || currentCount !== initialCount)
  );
}

/** Parses Attentive dataLayer payloads without type assertions. */
export function parseAttentiveEntry(entry: unknown): AttentiveEntry | null {
  if (entry == null || typeof entry !== "object" || Array.isArray(entry)) {
    return null;
  }

  const parsed: AttentiveEntry = {};
  for (const key of ATTENTIVE_ENTRY_KEYS) {
    const value = Reflect.get(entry, key);
    if (typeof value === "string") {
      parsed[key] = value;
    }
  }
  return parsed;
}

export function attentiveEventProps(
  entry: AttentiveEntry,
  { includeImpressionCount = false }: { includeImpressionCount?: boolean } = {}
): Record<string, unknown> {
  const props: Record<string, unknown> = {
    "creative id": entry.creative_id,
    "creative type": entry.creative_type,
    "page path": window.location.pathname,
    market: document.documentElement.dataset?.countryCode,
  };

  if (includeImpressionCount) {
    const impressionCount = readImpressionCountCookie();
    if (impressionCount !== undefined) {
      props["impression count"] = impressionCount;
    }
  }

  return props;
}

/** Fires view-popup props once, waiting briefly for attn_fullscreen_impression_count. */
export function fireViewPopupWhenImpressionCountReady(
  entry: AttentiveEntry,
  fire: (props: Record<string, unknown>) => void
): void {
  const buildProps = () =>
    attentiveEventProps(entry, { includeImpressionCount: true });
  const initialCount = readImpressionCountCookie();
  const waitMs =
    initialCount === undefined
      ? IMPRESSION_COUNT_WAIT_MS
      : IMPRESSION_COUNT_UPDATE_WAIT_MS;

  let fired = false;
  let timeoutId = 0;
  let onCookieChange: EventListener | undefined;

  const fireOnce = () => {
    if (fired) {
      return;
    }
    fired = true;
    clearTimeout(timeoutId);
    window.removeEventListener("pagehide", fireOnce);
    if (onCookieChange && window.cookieStore) {
      window.cookieStore.removeEventListener("change", onCookieChange);
    }
    fire(buildProps());
  };

  if (window.cookieStore) {
    onCookieChange = (event: Event) => {
      const changed = Reflect.get(event, "changed");
      if (!Array.isArray(changed)) {
        return;
      }
      const cookieUpdated = changed.some((cookie) => {
        if (cookie == null || typeof cookie !== "object") {
          return false;
        }
        return Reflect.get(cookie, "name") === IMPRESSION_COUNT_COOKIE_NAME;
      });
      if (
        cookieUpdated &&
        impressionCountIsReady(initialCount, readImpressionCountCookie())
      ) {
        fireOnce();
      }
    };
    window.cookieStore.addEventListener("change", onCookieChange);
  }

  window.addEventListener("pagehide", fireOnce, { once: true });
  timeoutId = window.setTimeout(fireOnce, waitMs);

  queueMicrotask(() => {
    if (impressionCountIsReady(initialCount, readImpressionCountCookie())) {
      fireOnce();
    }
  });
}

function bindDataLayerPush(dataLayer: {
  push: (...args: Record<string, unknown>[]) => number;
}): DataLayerPush {
  const nativePush = dataLayer.push.bind(dataLayer);
  return (...args: Record<string, unknown>[]) => nativePush(...args);
}

export function installDataLayerEntryListener(
  handleEntry: (entry: unknown) => void,
  state: DataLayerInstallState
): void {
  if (state.installed) {
    return;
  }
  state.installed = true;

  window.dataLayer = window.dataLayer || [];
  if (state.originalPush) {
    return;
  }

  state.originalPush = bindDataLayerPush(window.dataLayer);
  window.dataLayer.push = (...args: Record<string, unknown>[]) => {
    const result = state.originalPush!(...args);
    args.forEach(handleEntry);
    return result;
  };
  window.dataLayer.forEach(handleEntry);
}

export function resetDataLayerEntryListener(
  state: DataLayerInstallState
): void {
  if (state.originalPush && window.dataLayer) {
    window.dataLayer.push = state.originalPush;
    state.originalPush = null;
  }
  state.installed = false;
}
